"use client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { IconName, SizeProp } from "@fortawesome/fontawesome-svg-core";
import { ComponentProps } from "react";

import getIcon from "../lib/fontawesomeUtils";

type FontAwesomeIconProps = ComponentProps<typeof FontAwesomeIcon>;

/**
 * Icon component that renders FontAwesome icons using predefined names.
 *
 * Example usage:
 * <Icon name="user" />               // renders user icon
 * <Icon name="chart-line" />         // renders line chart icon
 * <Icon name="money-trend" />        // renders money trend icon
 *
 * The name prop should match one of the predefined icon names in fontawesomeUtils.
 * See fontawesomeUtils.tsx for full list of available icon names.
 *
 * Optional size prop accepts FontAwesome size values:
 * <Icon name="user" size="2x" />     // renders larger icon
 */

interface IconProps extends Omit<FontAwesomeIconProps, "icon"> {
  name: IconName;
  size?: SizeProp;
}

export default function Icon({ name, size = "lg" }: IconProps) {
  // Add error boundary or fallback for SSR
  if (typeof window === "undefined") {
    return null; // or a placeholder
  }

  return (
    <div
      className={`w-[${getSizeInPixels(size)}px] h-[${getSizeInPixels(size)}px]`}
    >
      <FontAwesomeIcon
        icon={getIcon(name) || getIcon("question")}
        size={size}
      />
    </div>
  );
}

const getSizeInPixels = (size: SizeProp): number => {
  switch (size) {
    case "2xs":
      return 12;
    case "xs":
      return 14;
    case "sm":
      return 16;
    case "lg":
      return 20;
    case "xl":
      return 24;
    case "2xl":
      return 32;
    case "1x":
      return 16;
    case "2x":
      return 32;
    case "3x":
      return 48;
    case "4x":
      return 64;
    case "5x":
      return 80;
    case "6x":
      return 96;
    case "7x":
      return 112;
    case "8x":
      return 128;
    case "9x":
      return 144;
    case "10x":
      return 160;
    default:
      return 18; // Default size for 'base'/'normal'
  }
};
